import { type Ref, onMounted, onUnmounted, ref } from 'vue'

/**
 * Глобальное реактивное «сейчас» (миллисекунды), пересчитываемое каждую минуту.
 * Используется в связке с {@link formatRelativeTime} — чтобы строки вида «5 мин назад»
 * сами обновлялись в шаблонах без перезагрузки данных.
 *
 * Внутри — один общий setInterval с подсчётом подписчиков:
 * - первый подписчик запускает тик;
 * - последний отписавшийся гасит интервал (никаких висящих таймеров после unmount).
 *
 * @example
 *   const now = useReactiveNow()
 *   const label = computed(() => formatRelativeTime(item.createdAt, now.value))
 */
const sharedNow = ref(Date.now())
let subscribers = 0
let intervalId: ReturnType<typeof setInterval> | null = null

const TICK_MS = 60_000

function startTickIfNeeded(): void {
  if (intervalId !== null) return
  intervalId = setInterval(() => {
    sharedNow.value = Date.now()
  }, TICK_MS)
}

function stopTickIfNoSubscribers(): void {
  if (subscribers > 0) return
  if (intervalId === null) return
  clearInterval(intervalId)
  intervalId = null
}

export function useReactiveNow(): Ref<number> {
  onMounted(() => {
    subscribers += 1
    sharedNow.value = Date.now() // на маунте сразу свежее значение
    startTickIfNeeded()
  })

  onUnmounted(() => {
    subscribers = Math.max(0, subscribers - 1)
    stopTickIfNoSubscribers()
  })

  return sharedNow
}
